import { Helmet } from 'react-helmet-async';

const BASE_URL = 'https://www.notematic.com.au';
const DEFAULT_OG_IMAGE = `${BASE_URL}/images/notematic-og.png`;

interface SeoHeadProps {
  title: string;
  description: string;
  canonical: string;
  ogImage?: string;
  /** Optional JSON-LD structured data object (will be serialised to application/ld+json) */
  jsonLd?: object;
}

/**
 * Per-page SEO head management using react-helmet-async.
 * Place inside any page component (inside PageLayout) to override the global defaults.
 */
export default function SeoHead({ title, description, canonical, ogImage, jsonLd }: SeoHeadProps) {
  const canonicalUrl = `${BASE_URL}${canonical}`;
  const imageUrl = ogImage ?? DEFAULT_OG_IMAGE;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="en_AU" />
      <meta property="og:site_name" content="Notematic" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {/* JSON-LD structured data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

